import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { map } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { SalesStatementService } from './sales-statement.service';
import { ToastrService } from 'ngx-toastr';
import { createEmptySalesStatementModel, SalesStatementListResponse, SalesStatementModel } from './sales-statement.model';
import { DatePipe } from '@angular/common';
import { PAGE_SIZE_DEFAULT, PAGE_SIZE_OPTIONS } from 'src/app/_common/constants';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { ConfigService } from 'src/app/_common/services/config.service';
import { TranslateService } from '@ngx-translate/core';
import { PreviewDialog, PreviewDialogData } from 'src/app/documents/preview-dialog/preview.dialog';
import { MatDialog } from '@angular/material/dialog';
import { ImageService } from 'src/app/_common/services/image.service';
import { TransformTypeEnum } from 'src/app/_common/enums/transform-type.enum';
import { faImage } from '@fortawesome/free-solid-svg-icons';

@Component({
  selector: 'app-sales-statement',
  templateUrl: './sales-statement.component.html',
  styleUrls: ['./sales-statement.component.scss']
})
export class SalesStatementComponent implements OnInit, OnDestroy {

  faPreview = faImage;

  @ViewChild(MatPaginator) paginator: MatPaginator;

  pageNumber: number = 0;
  pageSize: number = PAGE_SIZE_DEFAULT;
  pageSizeOptions: number[] = PAGE_SIZE_OPTIONS;
  totalRows: number = 0;

  containerTopValue: number = 0;

  pageEvent: PageEvent;

  displayedColumns = ['shopNumber', 'date', 'printerNumber', 'documentNumber', 'receiptCount'];
  summary: SalesStatementModel[] = [];

  printSubscription: Subscription;
  downloadSubscription: Subscription;
  downloadBasedOnItemsSubscription: Subscription;

  showTable: boolean = false;
  filters: any = {};

  totalSummary: SalesStatementModel = createEmptySalesStatementModel();

  activeVatRates = Object.entries(this.configService.settings.salesStatementSettings.activeVatRates);
  
  isPreviewed: number = null;
  previewDialogData: PreviewDialogData = { image: null, isReceipt: false, fileId: null, items: null }
  
  constructor(private salesStatementService: SalesStatementService,
    private configService: ConfigService,
    public dialog: MatDialog,
    public datepipe: DatePipe,
    private toastr: ToastrService,
    private translateService: TranslateService,
    private imageService: ImageService,

  ) {

    this.activeVatRates.forEach(([key, value]) => {
      this.displayedColumns.push('sale' + key.toUpperCase());
    })
    this.displayedColumns.push('gross');

    this.printSubscription = this.salesStatementService.printSalesStatement$.subscribe(() => {
      this.print();
    });

    this.downloadSubscription = this.salesStatementService.downloadSalesStatement$.subscribe((type) => {
      this.download(type);
    });

    this.downloadBasedOnItemsSubscription = this.salesStatementService.downloadSalesStatementBasedOnItems$.subscribe(() => {
      this.downloadBasedOnItems();
    });
  }

  updateTopValue(top: number) {
    this.containerTopValue = top;
  }

  calcContainerHeight(): string {
    return `calc(100% - ${this.containerTopValue}px)`;
  }

  updateDownloadBasedOnItemsButton(event: any) {
    this.filters = event;

    const isDateRangeSet: boolean = this.filters?.dateFrom?.trim() && this.filters?.dateTo?.trim();
    const isAnyPrinterSelected = this.filters?.printers?.length > 0;
    const isAnyShopSelected = this.filters?.shops?.length > 0;

    this.salesStatementService.canDownloadBasedOnItems = isDateRangeSet && isAnyPrinterSelected && isAnyShopSelected;
  }

  applyFilters(event: any) {
    this.filters = event;
    const receiptCountIndex = this.displayedColumns.indexOf('receiptCount');

    if (!this.filters.basedOnReceipts) {
      const documentNumberIndex = this.displayedColumns.indexOf('documentNumber');
      if (receiptCountIndex == -1) {
        this.displayedColumns.splice(documentNumberIndex + 1, 0, 'receiptCount');
      }
    } else {
      if (receiptCountIndex != -1) {
        this.displayedColumns.splice(receiptCountIndex, 1);
      }
    }
    this.loadData();
  }

  ngOnInit(): void {    
  }

  ngOnDestroy(): void {
    this.printSubscription.unsubscribe();
    this.downloadSubscription.unsubscribe();
    this.downloadBasedOnItemsSubscription.unsubscribe();
    this.salesStatementService.canDownload = false;
    this.salesStatementService.canPrint = false;
    this.salesStatementService.canDownloadBasedOnItems = false;
  }

  loadData() {
    this.salesStatementService.getSummary(this.filters)
      .pipe(map((res: SalesStatementListResponse) => {
        if (res.error.message) {
          this.toastr.error(res.error.message);
          return;
        }

        this.summary = res.model;

        if (this.summary.length > 0) {
          this.totalSummary = this.summary.slice(-1)[0];
          this.summary.pop();
        } else {
          this.totalSummary = createEmptySalesStatementModel();
        }

        const isSummaryNotEmpty: boolean = this.summary && this.summary.length > 0;
        const isDateRangeSet: boolean = this.filters?.dateFrom?.trim() && this.filters?.dateTo?.trim();
        const isAnyPrinterSelected = this.filters?.printers?.length > 0;
        const isAnyShopSelected = this.filters?.shops?.length > 0;

        this.salesStatementService.canDownload = isSummaryNotEmpty;
        this.salesStatementService.canPrint = isSummaryNotEmpty;
        this.salesStatementService.canDownloadBasedOnItems = isDateRangeSet && isAnyPrinterSelected && isAnyShopSelected;
        
        this.showTable = true;
      }))
      .subscribe();
  }

  print() {
    this.salesStatementService.downloadPdf(this.filters).subscribe(
      (response) => {
        var blob = new Blob([response.body], { type: 'application/pdf' });
        this.onPrint(blob);
      }
    );
  }

  onPrint(blob: Blob) {
    const fileURL = URL.createObjectURL(blob);

    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = fileURL;
    document.body.appendChild(iframe);

    iframe.onload = function () {
      iframe.contentWindow.print();
    };
  }

  download(type: string) {
    if (type == 'pdf') {
      this.salesStatementService.downloadPdf(this.filters).subscribe(
        response => {
          this.getDownload(type, response);
        });
    }
    else if (type == 'csv') {
      this.salesStatementService.downloadCsv(this.filters).subscribe(
        response => {
          this.getDownload(type, response);
        });
    }
    else {
      this.salesStatementService.downloadXlsx(this.filters).subscribe(
        response => {
          this.getDownload(type, response);
        });
    }
  }

  downloadBasedOnItems() {
    this.salesStatementService.downloadSalesStatementBasedOnItemsCsv(this.filters).subscribe(
        response => {
          this.getDownload('csv', response);
        });
  }

  private getDownload(type: string, response: any) {
    if (response.status == 204) {
      this.toastr.error(`${this.translateService.instant('common.error.downloading-file-failed').replace('{type}', type.toUpperCase())}`);
      return;
    }

    let mimeType;
    switch (type) {
      case 'pdf':
        mimeType = 'application/pdf';
        break;
      case 'csv':
        mimeType = 'text/csv';
        break;
      case 'xlsx':
        mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        break;
    }

    const blob = new Blob([response.body], { type: mimeType });
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = response.filename;
    anchor.click();
    window.URL.revokeObjectURL(url);
  }

  getColumnName(vatRate: string){
    return 'sale' + vatRate;
  }

  isSummaryForDay(element: any): boolean {
    return element.shopNumber && !element.printerNumber && element.date && !element.documentNumber;
  }

  isSummaryForPrinter(element: any): boolean {
    return element.shopNumber && element.printerNumber && element.date && !element.documentNumber;
  }

  isSummaryForShop(element: any): boolean {
    return element.shopNumber && !element.printerNumber && !element.date;
  }

  isTotalSummaryEmpty() {
    return JSON.stringify(this.totalSummary) == JSON.stringify(createEmptySalesStatementModel());
  }

  currentPreview(fileId: any) : boolean {
    return this.isPreviewed == fileId;
  }

  previewOpenDialog(element: any) {
    this.isPreviewed = element.fileId;
    var type = !this.filters.basedOnReceipts ? 7 : 5;

    this.imageService.getImage(element.fileId, type).subscribe(async response => {
      if (response.status === 204) {
        this.toastr.error(this.translateService.instant('documents.preview-dialog.load-error'));
        this.isPreviewed = null;
        return;
      }

      this.previewDialogData.image = response.body;
      this.previewDialogData.fileId = element.fileId;
      this.previewDialogData.items = this.summary.map(item => { return { fileId: item.fileId, type: type } });

      let dialogRef = this.dialog.open(PreviewDialog, {
        width: '420px',        
        height: '95%',
        data: this.previewDialogData,
        autoFocus: false,        
        panelClass: 'preview'
      });

      dialogRef.componentInstance.active.subscribe((fileId: any) => {
        this.isPreviewed = fileId;

        const activeElement = document.querySelector('.mat-row.active');
        if (activeElement) {
          activeElement.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'start' });
        }
      });

      dialogRef.afterClosed().subscribe(() => {
        //this.enableTextSelection(element);
        this.isPreviewed = null;
      });
    });
  }
}

